if (!customElements.get('ayne-kit-builder')) {
  customElements.define('ayne-kit-builder', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.selection = new Map();
      this.promotion = JSON.parse(this.querySelector('[data-kit-offers]').textContent);
      this.cards = [...this.querySelectorAll('[data-product-card]')];
      this.button = this.querySelector('[data-add-kit]');
      this.message = this.querySelector('[data-message]');
      this.formatter = new Intl.NumberFormat(this.dataset.locale || 'pt-BR', {style: 'currency', currency: this.dataset.currency || 'BRL'});
      this.cards.forEach(card => {
        card.variants = JSON.parse(card.querySelector('[data-variants]').textContent);
        this.updateCard(card);
      });
      const options = {signal: this.controller.signal};
      this.addEventListener('click', event => {
        const step = event.target.closest('[data-step]');
        if (step && !this.busy) {
          const card = step.closest('[data-product-card]');
          const input = card.querySelector('[data-quantity]');
          if (input.disabled) return;
          const variant = this.variant(card);
          const oldValue = Number(input.value) || 0;
          const next = Number(step.dataset.step) > 0 ? (oldValue === 0 ? variant.min : oldValue + variant.step) : oldValue - variant.step;
          input.value = next < variant.min ? 0 : next;
          this.select(card);
        }
        const remove = event.target.closest('[data-remove]');
        if (remove && !this.busy) {
          this.selection.delete(Number(remove.dataset.remove));
          this.cards.forEach(card => this.updateCard(card));
          this.render();
        }
      }, options);
      this.addEventListener('change', event => {
        const card = event.target.closest('[data-product-card]');
        if (!card || this.busy) return;
        if (event.target.matches('[data-variant]')) this.updateCard(card);
        if (event.target.matches('[data-quantity]')) this.select(card);
      }, options);
      this.button.addEventListener('click', () => this.add(), options);
      this.querySelectorAll('[data-enhanced]').forEach(el => el.hidden = false);
      this.render();
      this.couponsReady = window.AyneKitOffers.applyCodes(this.promotion).then(changed => {
        if (changed && this.isConnected) document.dispatchEvent(new CustomEvent('cart:refresh'));
      }).catch(() => {});
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    variant(card) { return card.variants.find(variant => variant.id === Number(card.querySelector('[data-variant]').value)); }
    money(cents) { return this.formatter.format(cents / 100); }
    updateCard(card) {
      const variant = this.variant(card);
      if (!variant) return;
      const input = card.querySelector('[data-quantity]');
      input.value = this.selection.get(variant.id)?.quantity || 0;
      input.disabled = !variant.available;
      input.step = variant.step;
      input.setAttribute('aria-label', `${card.dataset.title}${variant.title ? ` · ${variant.title}` : ''}`);
      if (variant.max != null) input.max = variant.max;
      else input.removeAttribute('max');
      card.querySelector('[data-card-price]').textContent = this.money(variant.price);
      card.querySelector('[data-stock]').hidden = variant.available;
      card.querySelector('[data-step="1"]').disabled = !variant.available || (variant.max != null && Number(input.value) >= variant.max);
      card.querySelector('[data-step="-1"]').disabled = Number(input.value) === 0;
      card.classList.toggle('is-selected', this.selection.has(variant.id));
    }
    select(card) {
      const input = card.querySelector('[data-quantity]');
      const variant = this.variant(card);
      const quantity = Number(input.value);
      const valid = Number.isSafeInteger(quantity) && quantity >= 0 && (quantity === 0 || (quantity >= variant.min && (quantity - variant.min) % variant.step === 0 && (variant.max == null || quantity <= variant.max)));
      if (!valid || !variant.available) {
        this.showMessage(this.dataset.invalid, true);
        this.updateCard(card);
        return false;
      }
      if (quantity) this.selection.set(variant.id, {...variant, quantity, label: `${card.dataset.title}${variant.title ? ` · ${variant.title}` : ''}`});
      else this.selection.delete(variant.id);
      this.showMessage('');
      this.updateCard(card);
      this.render();
      return true;
    }
    render() {
      const list = this.querySelector('[data-selected]');
      list.replaceChildren();
      const offer = window.AyneKitOffers.calculate([...this.selection.values()], this.promotion);
      const total = offer.total;
      const count = [...this.selection.values()].reduce((sum, item) => sum + item.quantity, 0);
      for (const item of offer.lines) {
        const row = document.createElement('li');
        const title = document.createElement('span');
        title.textContent = `${item.quantity} × ${item.label}`;
        const prices = document.createElement('div');
        prices.className = 'ak-line-prices';
        const originalTotal = item.price * item.quantity;
        const discounted = item.total < originalTotal;
        if (discounted) {
          const original = document.createElement('s');
          original.textContent = this.money(originalTotal);
          original.setAttribute('aria-label', `${this.dataset.regularPrice}: ${this.money(originalTotal)}`);
          prices.append(original);
        }
        const amount = document.createElement('strong');
        amount.textContent = this.money(item.total);
        amount.setAttribute('aria-label', `${discounted ? this.dataset.salePrice : this.dataset.regularPrice}: ${this.money(item.total)}`);
        prices.append(amount);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.remove = item.id;
        remove.textContent = this.dataset.remove;
        remove.setAttribute('aria-label', `${this.dataset.remove} ${item.label}`);
        row.append(title, prices, remove);
        list.append(row);
      }
      const countLabel = this.querySelector('[data-item-count]');
      countLabel.hidden = count === 0;
      countLabel.textContent = this.dataset.countLabel.replace('[count]', count);
      this.querySelector('[data-empty]').hidden = this.selection.size > 0;
      this.querySelector('[data-subtotal]').textContent = this.money(total);
      this.querySelector('[data-mobile-total]').textContent = this.money(total);
      const savings = this.querySelector('[data-savings]');
      if (savings) {
        savings.hidden = offer.savings === 0;
        savings.querySelector('strong').textContent = this.money(offer.savings);
      }
      this.querySelector('[data-mobile-bar]').hidden = this.selection.size === 0;
      this.button.disabled = this.selection.size === 0 || this.busy;
    }
    async add() {
      if (this.busy || this.selection.size === 0) return;
      for (const card of this.cards) {
        if (card.querySelector('[data-quantity]').disabled) continue;
        if (!this.select(card)) return;
      }
      if (!this.selection.size) return;
      this.busy = true;
      this.button.disabled = true;
      this.button.textContent = this.dataset.adding;
      this.setAttribute('aria-busy', 'true');
      const controls = [...this.querySelectorAll('[data-step], [data-quantity], [data-variant], [data-remove]')];
      const states = controls.map(control => control.disabled);
      controls.forEach(control => control.disabled = true);
      try {
        await this.couponsReady;
        await window.AyneKitOffers.applyCodes(this.promotion);
        const result = await window.AyneCart.add([...this.selection.values()].map(({id, quantity}) => ({id, quantity})), this.button, this.dataset.error);
        if (result) this.showMessage(this.dataset.added);
      } catch (error) {
        this.showMessage(error.message || this.dataset.error, true);
      } finally {
        this.busy = false;
        controls.forEach((control, index) => control.disabled = states[index]);
        this.button.disabled = this.selection.size === 0 || window.AyneCart.recovering;
        this.button.textContent = this.dataset.add;
        this.removeAttribute('aria-busy');
      }
    }
    showMessage(text, error = false) { this.message.textContent = text; this.message.dataset.error = String(error); }
  });
}
